// パネルの表示可否・並び順・サンプルグループ表示パネルの生成をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createPanelVisibilityService(deps) {
    const {
        DeepCopy,
        DicVal,
        G_id,
        GridData,
        hinbanFixEnabled,
        KAKOUSIJI_ANCHOR_PANEL_TYPE,
        KAKOUSIJI_HEADER_PANEL_TYPE,
        KAKOUSIJI_SAMPLE_GROUP_DISPLAY_PANEL_TYPES,
        KAKOUSIJI_SIJISU_TOTAL_PANEL_TYPE,
        SetGridData,
        activeKakousijiSummaryItem,
        columns,
        entrylock,
        fileRef,
        formstatus,
        getKakousijiEntryCache,
        imageRef,
        isEmpty,
        isKakousijiHeaderPanel,
        isKakousijiModeAnchorPanel,
        isKakousijiPanel,
        isKakousijiPanelType,
        isKakousijiSampleGroupSummaryItem,
        isKakousijiSamplePanel,
        kakouType,
        kakousijiPanelDisplayOrderValue,
        kakousijiSampleGroupEntryKeys,
        normalizeKakousijiDetailForHinbanPanels,
        panel,
        resolveKakousijiPanelView,
        setIsDragActive,
        tabletype,
        textfields,
        windowDimensions
    } = deps;

    function sectionDisplaySetting(panel) {
        const id = String(panel.id);
        const type = isEmpty(panel.type) ? "" : String(panel.type);

        if (id === "0" && type === "100") {
            return { showSection: true, showSettings: true, showEdit: false, showClose: true };
        }
        if (id === "0" && (["101", "102", "103", "104", "105"].includes(type) || isKakousijiPanelType(type))) {
            // NEW FORM では編集可/編集不可トグルを表示しない(セクションは常時編集可として扱う)
            return { showSection: true, showSettings: false, showEdit: formstatus !== "NEW FORM", showClose: true };
        }
        if (id === G_id.IMAGE && type === "-1") {
            return { showSection: true, showSettings: false, showEdit: true, showClose: false };
        }
        if ((id === "1" && type === "200") ||
            (id === "2" && type === "210") ||
            (id === "3" && type === "220") ||
            (id === "4" && type === "230")) {
            // NEW FORM では編集可/編集不可トグルを表示しない(セクションは常時編集可として扱う)
            return { showSection: true, showSettings: false, showEdit: formstatus !== "NEW FORM", showClose: false };
        }
        if (id === "5" && type === "240") {
            return { showSection: true, showSettings: false, showEdit: false, showClose: true };
        }
        return { showSection: true, showSettings: false, showEdit: false, showClose: false };
    }
    function onDragEnter(e) {
        if (entrylock) return;
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragActive(true);
        }
    };
    function FileDropClose() {
        setIsDragActive(false);
    }
    function panelnavwidth() {
        // 左: ENTRY FORM(StatusNav 約210px)+余白。右: チャット/レイアウト設定、
        // 品番確定ボタンが出る場合はその分も空ける(hinban-header-actions は right:15 の絶対配置)。
        const rightReserve = hinbanFixEnabled ? 220 : 120;
        return Math.max(windowDimensions.width - 230 - rightReserve, 240);
    }
    function EmptyPanel(id, type) {
        try {
            const sourceId = String(id ?? '').replace(/_sample_\d+$/, '');
            if (!panel.some(e => e.id === sourceId)) return true; //パネル定義なし
            if (!isEmpty(type)) {
                if (!panel.some(e => e.id === sourceId && e.type === type)) return true; //パネル定義なし
            }
            if (sourceId === G_id.BASE) return isEmpty(textfields.find(e => e.type === type));
            let tbltype = tabletype(sourceId);
            return !columns.some(e => e.type === tbltype);
        } catch { }
        return false;
    }
    function panelhidden(p) {
        if (isEmpty(p)) return false;
        const sectionSetting = sectionDisplaySetting(p);
        if (!sectionSetting.showSection) return true;
        // 複写(COPY FORM)も新規と同様に加工指示系セクション(サマリー含む)は表示しない。
        // 加工指示NOが複写元と同一のため、複写先では引き継がない(追加は別途操作で行う)。
        if ((formstatus === 'NEW FORM' || formstatus === 'COPY FORM') && (isKakousijiPanel(p) || String(DicVal(p, 'type', '')).trim() === KAKOUSIJI_ANCHOR_PANEL_TYPE)) return true;
        if (String(DicVal(p, 'type', '')).trim() === KAKOUSIJI_SIJISU_TOTAL_PANEL_TYPE) return true;
        if (isKakousijiHeaderPanel(p)) return true;
        if (p.id === G_id.IMAGE) {
            if (isEmpty(imageRef.current)) return true;
            if (imageRef.current.length === 0) return true;
            return p.hide;
        }
        if (p.id === G_id.FILE) {
            if (isEmpty(fileRef.current)) return true;
            if (fileRef.current.length === 0) return true;
            return p.hide;
        }
        const sourcePanel = sourcePanelForDisplayPanel(p);
        if (EmptyPanel(sourcePanel.id, sourcePanel.type)) return true;
        const activeSummaryItem = activeKakousijiSummaryItem();
        if (isKakousijiPanel(p) && !isKakousijiHeaderPanel(p) && !isKakousijiModeAnchorPanel(p)) {
            if (isEmpty(activeSummaryItem)) return true;
            const displayType = String(DicVal(p, 'type', '')).trim();
            const samplePanel = isKakousijiSamplePanel(p) || (isKakousijiSampleGroupSummaryItem(activeSummaryItem) && KAKOUSIJI_SAMPLE_GROUP_DISPLAY_PANEL_TYPES.includes(displayType));
            const sampleSelected = !!DicVal(activeSummaryItem, 'isSample', false);
            if (sampleSelected && !samplePanel) return true;
            if (!sampleSelected && samplePanel) return true;
        }
        if (isKakousijiPanel(p) && !isKakousijiHeaderPanel(p) && !isKakousijiModeAnchorPanel(p) && resolveKakousijiPanelView(p) !== kakouType) return true;
        return p.hide;
    }
    function isKakousijiSampleGroupActive() {
        return isKakousijiSampleGroupSummaryItem(activeKakousijiSummaryItem());
    }
    function sourcePanelForDisplayPanel(panelInfo) {
        if (isEmpty(panelInfo)) return panelInfo;
        const sourcePanelId = String(DicVal(panelInfo, 'sourcePanelId', '')).trim();
        if (sourcePanelId === '') return panelInfo;
        return { ...panelInfo, id: sourcePanelId };
    }
    function normalizeKakousijiSampleGroupDisplayRows(snapshot, panelType) {
        const detail = normalizeKakousijiDetailForHinbanPanels(DicVal(snapshot, 'detail', {}));
        return DeepCopy(DicVal(detail, panelType, []));
    }
    function buildKakousijiSampleGroupDisplayPanels(basePanels) {
        const activeItem = activeKakousijiSummaryItem();
        if (!isKakousijiSampleGroupSummaryItem(activeItem)) return basePanels;
        const entryKeys = kakousijiSampleGroupEntryKeys(activeItem);
        if (entryKeys.length === 0) return basePanels;
        const panelByType = {};
        (Array.isArray(basePanels) ? basePanels : []).forEach((panelInfo) => {
            const type = String(DicVal(panelInfo, 'type', '')).trim();
            if (KAKOUSIJI_SAMPLE_GROUP_DISPLAY_PANEL_TYPES.includes(type) && isEmpty(panelByType[type])) {
                panelByType[type] = panelInfo;
            }
        });
        const samplePanels = [];
        entryKeys.forEach((entryKey, entryIndex) => {
            const snapshot = getKakousijiEntryCache(entryKey);
            const snapshotData = DicVal(snapshot, 'data', {});
            const ssdNo = String(DicVal(snapshotData, 'SSD_NO', DicVal(snapshotData, 'SSD_SIJINO', entryKey))).trim();
            KAKOUSIJI_SAMPLE_GROUP_DISPLAY_PANEL_TYPES.forEach((type) => {
                const sourcePanel = panelByType[type];
                if (isEmpty(sourcePanel)) return;
                const displayPanel = {
                    ...sourcePanel,
                    id: `${DicVal(sourcePanel, 'id', '')}_sample_${entryIndex}`,
                    sourcePanelId: DicVal(sourcePanel, 'id', ''),
                    sampleEntryKey: entryKey,
                    sampleEntryIndex: entryIndex,
                    sampleSsdNo: ssdNo,
                    sampleDisplayKey: `sample_${entryIndex}_${DicVal(sourcePanel, 'id', '')}_${type}`,
                    name2: DicVal(sourcePanel, 'name2', '')
                };
                if (!Array.isArray(GridData(displayPanel))) SetGridData(displayPanel, normalizeKakousijiSampleGroupDisplayRows(snapshot, type));
                samplePanels.push(displayPanel);
            });
        });
        return (Array.isArray(basePanels) ? basePanels : []).filter((panelInfo) => {
            const type = String(DicVal(panelInfo, 'type', '')).trim();
            return !KAKOUSIJI_SAMPLE_GROUP_DISPLAY_PANEL_TYPES.includes(type);
        }).concat(samplePanels);
    }
    function orderedPanels() {
        let ordered = [...panel];
        const imageIndex = ordered.findIndex(p => p.id === G_id.IMAGE);
        const type102Index = ordered.findIndex(p => p.type === "102");
        const type200Index = ordered.findIndex(p => p.type === "200");

        if (imageIndex !== -1 && type102Index !== -1 && type200Index !== -1 && !(imageIndex > type102Index && imageIndex < type200Index)) {
            const imagePanel = ordered[imageIndex];
            ordered = ordered.filter((_, index) => index !== imageIndex);
            const insertIndex = ordered.findIndex(p => p.type === "102");
            ordered.splice(insertIndex + 1, 0, imagePanel);
        }

        const kakousijiHeaderPanel = ordered.find((p) => String(DicVal(p, "type", "")).trim() === KAKOUSIJI_HEADER_PANEL_TYPE) || null;
        const kakousijiDetailPanels = ordered
            .filter((p) => isKakousijiPanel(p) && String(DicVal(p, "type", "")).trim() !== KAKOUSIJI_HEADER_PANEL_TYPE)
            .sort((a, b) => kakousijiPanelDisplayOrderValue(a) - kakousijiPanelDisplayOrderValue(b));
        const kakousijiPanels = kakousijiHeaderPanel === null
            ? kakousijiDetailPanels
            : [kakousijiHeaderPanel, ...kakousijiDetailPanels];
        if (kakousijiPanels.length === 0) return buildKakousijiSampleGroupDisplayPanels(ordered);

        const nonKakousijiPanels = ordered.filter((p) => !isKakousijiPanel(p));
        const anchorIndex = nonKakousijiPanels.findIndex((p) => String(DicVal(p, "type", "")) === KAKOUSIJI_ANCHOR_PANEL_TYPE);
        if (anchorIndex === -1) {
            return buildKakousijiSampleGroupDisplayPanels([...nonKakousijiPanels, ...kakousijiPanels]);
        }
        nonKakousijiPanels.splice(anchorIndex + 1, 0, ...kakousijiPanels);
        return buildKakousijiSampleGroupDisplayPanels(nonKakousijiPanels);
    }

    return {
        sectionDisplaySetting,
        onDragEnter,
        FileDropClose,
        panelnavwidth,
        EmptyPanel,
        panelhidden,
        isKakousijiSampleGroupActive,
        sourcePanelForDisplayPanel,
        normalizeKakousijiSampleGroupDisplayRows,
        buildKakousijiSampleGroupDisplayPanels,
        orderedPanels
    };
}
